// The scheduling pages' pure helpers (lane B5 S12, screens-walks §2.3–§2.7): which request a page reads, the proposed
// slots as the design system's Slot items with the conflict each one breaks ("A detected conflict never hides the
// slot"), and the one-day free/busy grid of the chosen slot — every time first in the event's time zone ("Store
// timestamps in UTC and preserve event time zone."), an attendee's local time only where it differs.

import type { AttendeeAvailabilityOut, MeetingOut, SchedulingOpenInput, SchedulingOpenOutput } from "@/api/tools";
import { SCHEDULING_LABELS, formatDateTime, type AvailRow, type AvailState, type Slot } from "@/mc";
import { wallTime, zonedInstant } from "./dialogs";
import { ROLE_WORD, conflictWord } from "./workbench";

/** The page a scheduling card sits on: a candidate's round (screen, HM interview, one Final Interview Loop round), the
 *  intake call, or the debrief. */
export type ScheduleSubject =
  | { kind: "round"; rid: string; aid: string; roundKind: "recruiter_screen" | "hm_round" | "onsite" | "additional"; roundDefId?: string | undefined }
  | { kind: "intake"; rid: string }
  | { kind: "debrief"; rid: string; cid: string };

/** The `scheduling.open` input that names the subject's request. */
export function openInput(subject: ScheduleSubject): SchedulingOpenInput {
  switch (subject.kind) {
    case "intake":
      return { meeting_type: "intake", requisition_id: subject.rid };
    case "debrief":
      return { meeting_type: "debrief", requisition_id: subject.rid, cycle_id: subject.cid };
    default:
      return {
        application_id: subject.aid,
        meeting_type: subject.roundKind === "recruiter_screen" ? "recruiter_screen" : subject.roundKind === "hm_round" ? "hm_interview" : "final_loop",
      };
  }
}

/** The query key the subject's request is read (and invalidated) under. */
export const openKey = (subject: ScheduleSubject): readonly unknown[] => ["scheduling.open", openInput(subject)];

/** The meetings of this subject: a loop round's page shows only its own round's meeting. */
export function meetingsOf(request: SchedulingOpenOutput, subject: ScheduleSubject): MeetingOut[] {
  const live = request.meetings.filter((m) => m.state !== "cancelled");
  if (subject.kind === "round" && subject.roundDefId) return live.filter((m) => m.round_def_id === subject.roundDefId);
  return live;
}

/**
 * The window a Final Interview Loop round's times are proposed in: after every earlier round that is booked and before
 * every later one ("Prevent invalid sequence."), so a slot the loop's order would refuse is never offered. Empty for
 * any other meeting, and for a round with no booked neighbour.
 */
export function sequenceWindow(request: SchedulingOpenOutput, roundDefId: string | null | undefined): { earliest?: string; latest?: string } {
  const rounds = request.loop?.rounds ?? [];
  const mine = rounds.find((r) => r.round_def_id === roundDefId);
  if (!mine) return {};
  const live = request.meetings.filter((m) => m.state !== "cancelled" && m.starts_at && m.ends_at);
  const at = (rd: string) => live.find((m) => m.round_def_id === rd);
  const before = rounds.filter((r) => r.sequence < mine.sequence).map((r) => at(r.round_def_id)?.ends_at).filter((x): x is string => !!x);
  const after = rounds.filter((r) => r.sequence > mine.sequence).map((r) => at(r.round_def_id)?.starts_at).filter((x): x is string => !!x);
  const out: { earliest?: string; latest?: string } = {};
  if (before.length) out.earliest = new Date(Math.max(Date.now(), ...before.map((x) => Date.parse(x)))).toISOString();
  if (after.length) out.latest = new Date(Math.min(...after.map((x) => Date.parse(x)))).toISOString();
  return out;
}

/** A time proposed for the meeting. */
export interface ProposedTime {
  start: string;
  end: string;
}

const overlaps = (a0: number, a1: number, b0: number, b1: number) => a0 < b1 && b0 < a1;

/** A conflict an attendee has with a slot: their role, the kind (busy, tentative, oof, outside_hours) and whether the
 *  meeting needs them. */
export interface SlotConflict {
  role: string;
  kind: string;
  required: boolean;
}

/** The attendees whose busy time (busy, tentative or OOF) overlaps a slot, or who are outside their hours, with the kind
 *  of conflict. */
export function conflictsAt(slot: ProposedTime, attendees: readonly AttendeeAvailabilityOut[]): SlotConflict[] {
  const s = Date.parse(slot.start);
  const e = Date.parse(slot.end);
  const out: SlotConflict[] = [];
  for (const a of attendees) {
    const hit = a.busy.find((b) => overlaps(s, e, Date.parse(b.start), Date.parse(b.end)));
    if (hit) out.push({ role: a.role, kind: hit.kind === "oof" || hit.kind === "tentative" ? hit.kind : "busy", required: a.required });
    else if (!inHours(a, slot.start) || !inHours(a, new Date(e - 60_000).toISOString())) {
      out.push({ role: a.role, kind: "outside_hours", required: a.required });
    }
  }
  return out;
}

/** The conflicts booking refuses without *Conflict* and a reason, by the published conflict policy the server applies:
 *  an optional attendee's clash follows `optional_attendee`, a required attendee's its kind; `warn` ones are shown on
 *  the slot but ask for nothing. A kind the policy does not name asks for a reason, as the server does. */
export function blockingAt(slot: ProposedTime, attendees: readonly AttendeeAvailabilityOut[], policy: Readonly<Record<string, string>>): SlotConflict[] {
  return conflictsAt(slot, attendees).filter((c) => (policy[c.required ? c.kind : "optional_attendee"] ?? "reason") === "reason");
}

/** Whether an instant falls inside the attendee's working day and hours, on their own clock. */
function inHours(a: AttendeeAvailabilityOut, at: string): boolean {
  const wall = wallTime(at, a.timezone);
  const day = new Date(`${wall.slice(0, 10)}T12:00:00Z`).getUTCDay() || 7; // ISO weekday, Monday = 1
  const hm = wall.slice(11, 16);
  return a.work_days.includes(day) && hm >= a.work_start && hm < a.work_end;
}

/** The time of day on the attendee's own clock, when their zone differs from the event's ("9:00 AM EDT · 8:00 AM CDT for
 *  {name}"). */
function localLine(slot: ProposedTime, eventTz: string, attendees: readonly AttendeeAvailabilityOut[]): string | undefined {
  const other = attendees.find((a) => a.timezone && a.timezone !== eventTz);
  return other ? `${formatDateTime(slot.start, other.timezone)} · ${ROLE_WORD[other.role] ?? other.role}` : undefined;
}

/** The proposed times as Slot items: the time and zone, the attendee's local time, and the conflict a slot breaks. */
export function slotItems(slots: readonly ProposedTime[], eventTz: string, attendees: readonly AttendeeAvailabilityOut[]): Slot[] {
  return slots.map((slot, index) => {
    const found = conflictsAt(slot, attendees);
    return {
      id: slot.start,
      when: formatDateTime(slot.start, eventTz),
      zone: eventTz,
      local: localLine(slot, eventTz, attendees),
      conflict: found.length ? { reason: found.map((c) => `${conflictWord(c.kind)} · ${ROLE_WORD[c.role] ?? c.role}`).join(", ") } : undefined,
      hs: `sched.slot.${index}`,
    };
  });
}

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17] as const;

/** The chosen slot's day as an hour-by-hour free/busy grid in the event's zone: a row per attendee (their zone under the
 *  name), each cell free, busy, OOF or outside their working hours, and the slot's hours selected. */
export function dayGrid(slot: ProposedTime, eventTz: string, attendees: readonly AttendeeAvailabilityOut[]): {
  columns: string[];
  rows: AvailRow[];
  selected: { from: number; to: number } | undefined;
} {
  const day = wallTime(slot.start, eventTz).slice(0, 10);
  const cells = HOURS.map((h) => {
    const start = zonedInstant(`${day}T${String(h).padStart(2, "0")}:00`, eventTz) ?? slot.start;
    return { start, end: new Date(Date.parse(start) + 3_600_000).toISOString() };
  });
  const columns = cells.map((c) => wallTime(c.start, eventTz).slice(11, 16));
  const rows: AvailRow[] = attendees.map((a) => ({
    name: ROLE_WORD[a.role] ?? a.role,
    zone: a.timezone,
    cells: cells.map((c): AvailState => {
      const hit = a.busy.find((b) => overlaps(Date.parse(c.start), Date.parse(c.end), Date.parse(b.start), Date.parse(b.end)));
      if (hit) return hit.kind === "oof" ? "oof" : "busy";
      return inHours(a, c.start) ? "free" : "out";
    }),
  }));
  const s = Date.parse(slot.start);
  const e = Date.parse(slot.end);
  const hit = cells.map((c, i) => (overlaps(s, e, Date.parse(c.start), Date.parse(c.end)) ? i : -1)).filter((i) => i >= 0);
  return { columns, rows, selected: hit.length ? { from: hit[0]!, to: hit[hit.length - 1]! } : undefined };
}

/** The request's earliest and latest proposed times, widened to whole days, as the free/busy window. */
export function windowOf(slots: readonly ProposedTime[]): { earliest: string; latest: string } | null {
  if (!slots.length) return null;
  const starts = slots.map((s) => Date.parse(s.start));
  const ends = slots.map((s) => Date.parse(s.end));
  const day = 86_400_000;
  return {
    earliest: new Date(Math.min(...starts) - day / 2).toISOString(),
    latest: new Date(Math.max(...ends) + day / 2).toISOString(),
  };
}

/** "{n} proposed slots" (G §9). */
export const proposedCount = (n: number): string => `${n} ${SCHEDULING_LABELS.proposedSlots}`;

/**
 * The cause a request for a new time carries (`candidate · panel · process`; W-Q7's reason list is still open, so no cause
 * word is drawn): a Hiring Manager or an Interviewer asking about their own meeting is the panel, anyone else the process.
 */
export function causeOf(roles: readonly string[]): "panel" | "process" {
  return roles.includes("hm") || roles.includes("interviewer") ? "panel" : "process";
}
